import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { apiService } from "../../api/services/apiService";
import { useGatewaySkus } from "../../hooks/useGatewaySkus";
import type { Product, ProductGatewaySkuAttachment } from "../../types";
import { GatewaySkuStatus } from "../../types";
import { gatewayTypeLabel } from "../../utils/gatewayType";

interface ProductGatewaySkuSectionProps {
  product: Product | null;
  onProductUpdated: (product: Product) => void;
}

const ProductGatewaySkuSection: React.FC<ProductGatewaySkuSectionProps> = ({
  product,
  onProductUpdated,
}) => {
  const { gatewaySkus, loading: skusLoading } = useGatewaySkus();
  const [attachments, setAttachments] = useState<ProductGatewaySkuAttachment[]>([]);
  const [subscribable, setSubscribable] = useState(false);
  const [selectedSkuUuid, setSelectedSkuUuid] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshProduct = useCallback(async () => {
    if (!product?.uuid) return;
    try {
      const full = await apiService.getProduct(product.uuid);
      setAttachments(full.gatewaySkuAttachments ?? []);
      setSubscribable(full.subscribable === true);
      onProductUpdated(full);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load gateway SKU attachments");
    }
  }, [product?.uuid, onProductUpdated]);

  useEffect(() => {
    setAttachments(product?.gatewaySkuAttachments ?? []);
    setSubscribable(product?.subscribable === true);
  }, [product]);

  const availableSkus = useMemo(() => {
    const attached = new Set(attachments.map((a) => a.gatewaySku.uuid));
    return gatewaySkus.filter((sku) => !attached.has(sku.uuid));
  }, [gatewaySkus, attachments]);

  const handleAttach = async () => {
    if (!product?.uuid || !selectedSkuUuid) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.attachGatewaySkuToProduct(
        product.uuid,
        selectedSkuUuid,
        attachmentName.trim() || undefined,
      );
      setSelectedSkuUuid("");
      setAttachmentName("");
      await refreshProduct();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to attach gateway SKU");
    } finally {
      setBusy(false);
    }
  };

  const handleDetach = async (gatewaySkuUuid: string) => {
    if (!product?.uuid) return;
    setBusy(true);
    setError(null);
    try {
      await apiService.detachGatewaySkuFromProduct(product.uuid, gatewaySkuUuid);
      await refreshProduct();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to detach gateway SKU");
    } finally {
      setBusy(false);
    }
  };

  if (!product?.uuid) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Gateway SKUs
        </Typography>
        <Chip
          size="small"
          label={subscribable ? "Subscribable" : "Not subscribable"}
          color={subscribable ? "success" : "default"}
          variant={subscribable ? "filled" : "outlined"}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Attach at least one active gateway SKU so developers can subscribe to this product.
      </Typography>

      {attachments.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No gateway SKUs attached.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {attachments.map((row) => (
            <Box
              key={row.uuid}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {row.attachmentName || row.gatewaySku.gatewayCommonName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {gatewayTypeLabel(row.gatewaySku.gatewayType)} · {row.gatewaySku.gatewayId} ·{" "}
                  {row.gatewaySku.envName} ·{" "}
                  {row.gatewaySku.skuStatus}
                  {row.gatewaySku.skuStatus !== GatewaySkuStatus.ACTIVE
                    ? " (does not enable subscription)"
                    : ""}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Detach gateway SKU"
                onClick={() => handleDetach(row.gatewaySku.uuid)}
                disabled={busy}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
        <TextField
          select
          size="small"
          label="Gateway SKU"
          value={selectedSkuUuid}
          onChange={(e) => setSelectedSkuUuid(e.target.value)}
          disabled={busy || skusLoading || availableSkus.length === 0}
          sx={{ minWidth: 220, flex: 1 }}
        >
          {availableSkus.map((sku) => (
            <MenuItem key={sku.uuid} value={sku.uuid}>
              {sku.gatewayCommonName} ({gatewayTypeLabel(sku.gatewayType)}, {sku.envName})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Attachment name (optional)"
          value={attachmentName}
          onChange={(e) => setAttachmentName(e.target.value)}
          disabled={busy}
          sx={{ minWidth: 200, flex: 1 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={handleAttach}
          disabled={busy || !selectedSkuUuid}
        >
          Attach
        </Button>
      </Stack>

      {availableSkus.length === 0 && !skusLoading && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Create gateway SKUs in the Gateway SKU Hub before attaching.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default ProductGatewaySkuSection;
